/*
  MORI-specific product media enhancements.
  - Keeps color-linked media filtering separate from the core ProductInfo flow.
  - Keeps gallery DOM sync logic separate so Dawn/Rise upgrades can review this file in isolation.
*/
(function () {
  class ProductGalleryDomSyncController {
    constructor(host) {
      this.host = host;
    }

    isElementVisible(element) {
      if (!element || !element.isConnected) return false;
      const view = element.ownerDocument?.defaultView || window;
      const style = view.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    getVisibleMatch(root, selector) {
      const candidates = Array.from(root.querySelectorAll(selector));
      return candidates.find((candidate) => this.isElementVisible(candidate)) || null;
    }

    getPreferredGalleryList(root = this.host) {
      const visibleViewer = this.getVisibleMatch(root, 'media-gallery [id^="GalleryViewer"]');
      if (visibleViewer) {
        return visibleViewer.querySelector('ul[id^="Slider-Gallery-"]') || visibleViewer.querySelector('ul');
      }
      return (
        root.querySelector('media-gallery [id^="GalleryViewer"] ul[id^="Slider-Gallery-"]') ||
        root.querySelector('media-gallery ul[id^="Slider-Gallery-"]') ||
        root.querySelector('media-gallery ul')
      );
    }

    getPreferredThumbnailList(root = this.host) {
      const visibleThumbs = this.getVisibleMatch(root, 'media-gallery [id^="GalleryThumbnails"]');
      if (visibleThumbs) {
        return visibleThumbs.querySelector('ul[id^="Slider-Thumbnails-"]') || visibleThumbs.querySelector('ul');
      }
      return (
        root.querySelector('media-gallery [id^="GalleryThumbnails"] ul[id^="Slider-Thumbnails-"]') ||
        root.querySelector('media-gallery ul[id^="Slider-Thumbnails-"]')
      );
    }

    syncDuplicateMediaListMarkup() {
      const syncSelector = (containerSelector, listSelector) => {
        const containers = Array.from(this.host.querySelectorAll(`media-gallery ${containerSelector}`));
        if (containers.length < 2) return;

        const listEntries = containers
          .map((container) => ({ container, list: container.querySelector(listSelector) }))
          .filter((entry) => entry.list);
        if (listEntries.length < 2) return;

        const canonicalEntry =
          listEntries.find((entry) => !this.isElementVisible(entry.container)) || listEntries[0];
        if (!canonicalEntry?.list) return;

        listEntries
          .filter((entry) => this.isElementVisible(entry.container))
          .forEach((entry) => {
            if (entry.list.innerHTML === canonicalEntry.list.innerHTML) return;
            entry.list.innerHTML = canonicalEntry.list.innerHTML;
          });
      };

      syncSelector('[id^="GalleryViewer"]', 'ul[id^="Slider-Gallery-"], ul');
      syncSelector('[id^="GalleryThumbnails"]', 'ul[id^="Slider-Thumbnails-"], ul');
    }

    getRawMediaId(value) {
      const mediaId = String(value || '');
      if (!mediaId) return '';
      const sectionPrefix = `${this.host.dataset.section || ''}-`;
      if (sectionPrefix !== '-' && mediaId.startsWith(sectionPrefix)) {
        return mediaId.slice(sectionPrefix.length);
      }
      return mediaId.split('-').pop() || mediaId;
    }

    getMediaListSignature(list) {
      if (!list) return '';
      const selector = list.id && list.id.indexOf('Slider-Thumbnails-') === 0 ? '[data-target]' : '[data-media-id]';
      return Array.from(list.querySelectorAll(selector))
        .map((node) => this.getRawMediaId(node.getAttribute('data-target') || node.getAttribute('data-media-id')))
        .filter(Boolean)
        .join('|');
    }

    getMediaSetSignature(root) {
      if (!root) return '';
      return Array.from(root.querySelectorAll('[data-media-id]'))
        .map((node) => this.getRawMediaId(node.getAttribute('data-media-id')))
        .filter(Boolean)
        .filter((id, index, ids) => ids.indexOf(id) === index)
        .sort()
        .join('|');
    }

    updateMedia(html, variantFeaturedMediaId) {
      if (!variantFeaturedMediaId) return;

      const mediaGalleryDestination = this.getPreferredGalleryList(html);
      const thumbnailGalleryDestination = this.getPreferredThumbnailList(html);
      const syncAllListMarkup = (selector, destinationList) => {
        if (!destinationList) return;
        const destinationSignature = this.getMediaListSignature(destinationList);
        const sourceLists = Array.from(this.host.querySelectorAll(selector));
        sourceLists.forEach((sourceList) => {
          if (destinationSignature && this.getMediaListSignature(sourceList) === destinationSignature) return;
          if (sourceList.innerHTML === destinationList.innerHTML) return;
          sourceList.innerHTML = destinationList.innerHTML;
        });
      };

      if (mediaGalleryDestination) {
        if (this.host.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
        syncAllListMarkup('media-gallery ul[id^="Slider-Gallery-"]', mediaGalleryDestination);
      }

      if (thumbnailGalleryDestination) {
        syncAllListMarkup('media-gallery ul[id^="Slider-Thumbnails-"]', thumbnailGalleryDestination);
      }

      const shouldForceFeaturedMedia = !this.host.getColorOptionContext()?.selectedValue;
      if (shouldForceFeaturedMedia) {
        this.host.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.host.dataset.section}-${variantFeaturedMediaId}`,
          true
        );
      }

      const modalContent = this.host.productModal?.querySelector(`.product-media-modal__content`);
      const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
      if (modalContent && newModalContent) {
        const sameModalMediaSet =
          this.getMediaSetSignature(modalContent) &&
          this.getMediaSetSignature(modalContent) === this.getMediaSetSignature(newModalContent);
        if (
          !sameModalMediaSet &&
          (!this.host.optimizeVariantDomUpdates || modalContent.innerHTML !== newModalContent.innerHTML)
        ) {
          modalContent.innerHTML = newModalContent.innerHTML;
        }
      }
    }
  }

  class ProductColorMediaFilterController {
    constructor(host) {
      this.host = host;
      this.deferredFilterRafId = 0;
      this.deferredFilterTimeoutId = 0;
      this.pendingFilterVariant = null;
    }

    scheduleDeferredColorFilter(variant = null) {
      this.pendingFilterVariant = variant;
      if (this.deferredFilterRafId || this.deferredFilterTimeoutId) return;

      this.deferredFilterRafId = requestAnimationFrame(() => {
        this.deferredFilterRafId = 0;
        this.deferredFilterTimeoutId = window.setTimeout(() => {
          this.deferredFilterTimeoutId = 0;
          const nextVariant = this.pendingFilterVariant;
          this.pendingFilterVariant = null;
          if (!this.host.isConnected) return;
          this.applyGalleryColorFilter(nextVariant || this.host.getSelectedVariant(this.host));
        }, 0);
      });
    }

    getColorOptionContext(variant = null) {
      const variantRoot = this.host.variantSelectors;
      if (!variantRoot) return null;

      const isColorName = (name) => /(color|colour|\u30ab\u30e9\u30fc|\u8272)/i.test(String(name || '').trim());
      const groups = Array.from(variantRoot.querySelectorAll('.product-form__input'));
      for (let index = 0; index < groups.length; index++) {
        const group = groups[index];
        const legend = group.querySelector('legend');
        const label = group.querySelector('label.form__label');
        const name = (legend?.textContent || label?.textContent || '').replace(':', '').trim();
        if (!isColorName(name)) continue;

        const radioOptions = Array.from(group.querySelectorAll('input[type="radio"][value]'));
        if (radioOptions.length) {
          const values = radioOptions.map((input) => input.value).filter(Boolean);
          const selectedInput = group.querySelector('input[type="radio"]:checked');
          const selectedValue =
            (Array.isArray(variant?.options) ? variant.options[index] : '') || selectedInput?.value || '';
          if (values.length && selectedValue) {
            return { values, selectedValue };
          }
        }

        const select = group.querySelector('select');
        if (!select) continue;
        const options = Array.from(select.options);
        const values = options.map((opt) => opt.value).filter(Boolean);
        const selectedValue = (Array.isArray(variant?.options) ? variant.options[index] : '') || select.value || '';
        if (values.length && selectedValue) {
          return { values, selectedValue };
        }
      }

      return null;
    }

    getVariantMediaGroupingData() {
      const script = this.host.querySelector('script[data-variant-media-groups]');
      const scriptContent = script?.textContent?.trim() || '';
      if (!scriptContent) {
        this.host.variantMediaGroupingData = null;
        this.host.variantMediaGroupingDataSource = '';
        return null;
      }
      if (this.host.variantMediaGroupingData && this.host.variantMediaGroupingDataSource === scriptContent) {
        return this.host.variantMediaGroupingData;
      }
      try {
        const parsed = JSON.parse(scriptContent);
        const orderedMediaIds = Array.isArray(parsed?.orderedMediaIds)
          ? parsed.orderedMediaIds.map((id) => String(id)).filter(Boolean)
          : [];
        const variantFeaturedMediaIds = Array.isArray(parsed?.variantFeaturedMediaIds)
          ? parsed.variantFeaturedMediaIds.map((id) => String(id)).filter(Boolean)
          : [];
        const variantRecords = Array.isArray(parsed?.variantRecords)
          ? parsed.variantRecords
              .map((record) => {
                const id = record?.id ? String(record.id) : '';
                const featuredMediaId = record?.featuredMediaId ? String(record.featuredMediaId) : '';
                const options = Array.isArray(record?.options)
                  ? record.options.map((option) => String(option || ''))
                  : [];
                if (!id || !featuredMediaId) return null;
                return { id, featuredMediaId, options };
              })
              .filter(Boolean)
          : [];
        this.host.variantMediaGroupingData = {
          orderedMediaIds,
          variantFeaturedMediaIds,
          variantRecords,
        };
        this.host.variantMediaGroupingDataSource = scriptContent;
        return this.host.variantMediaGroupingData;
      } catch (error) {
        this.host.variantMediaGroupingData = null;
        this.host.variantMediaGroupingDataSource = '';
        console.warn('[product-info] failed to parse variant media groups', error);
        return null;
      }
    }

    getOrderedVariantMediaContext(variant = null) {
      const featuredMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : '';
      if (!featuredMediaId) return null;

      const data = this.getVariantMediaGroupingData();
      if (!data?.orderedMediaIds?.length) return null;
      const selectedIndex = data.orderedMediaIds.indexOf(featuredMediaId);
      if (selectedIndex === -1) return null;

      const featuredSet = new Set(data.variantFeaturedMediaIds);
      const exactIds = [];
      for (let index = selectedIndex; index < data.orderedMediaIds.length; index++) {
        const mediaId = data.orderedMediaIds[index];
        if (index > selectedIndex && featuredSet.has(mediaId)) break;
        exactIds.push(mediaId);
      }
      if (!exactIds.length) return null;

      return {
        exactIds,
        exactIdSet: new Set(exactIds),
      };
    }

    buildColorMediaLookup(variantMediaGroupingData = null, colorValues = [], normalize = null) {
      const data = variantMediaGroupingData;
      const normalizeValue =
        normalize ||
        ((value) =>
          String(value || '')
            .trim()
            .toLowerCase());
      const normalizedColorValues = Array.isArray(colorValues)
        ? colorValues.map(normalizeValue).filter(Boolean)
        : [];
      if (!data?.orderedMediaIds?.length || !normalizedColorValues.length || !Array.isArray(data?.variantRecords)) {
        return null;
      }

      const orderedMediaIds = data.orderedMediaIds;
      const featuredSet = new Set(data.variantFeaturedMediaIds || []);
      const firstVariantMediaIndex = orderedMediaIds.findIndex((mediaId) => featuredSet.has(mediaId));
      const sharedMediaIds = firstVariantMediaIndex > 0 ? orderedMediaIds.slice(0, firstVariantMediaIndex) : [];
      const colorMediaMap = {};
      const variantToColorMap = {};

      data.variantRecords.forEach((record) => {
        const variantId = String(record?.id || '');
        const featuredMediaId = String(record?.featuredMediaId || '');
        if (!variantId || !featuredMediaId) return;

        const matchedColors = (Array.isArray(record?.options) ? record.options : [])
          .map(normalizeValue)
          .filter((value) => normalizedColorValues.includes(value));
        if (!matchedColors.length) return;

        const variantColor = matchedColors.sort((a, b) => b.length - a.length)[0];
        variantToColorMap[variantId] = variantColor;

        const startIndex = orderedMediaIds.indexOf(featuredMediaId);
        if (startIndex === -1) return;

        if (!colorMediaMap[variantColor]) {
          colorMediaMap[variantColor] = new Set();
        }
        for (let index = startIndex; index < orderedMediaIds.length; index++) {
          const mediaId = orderedMediaIds[index];
          if (index > startIndex && featuredSet.has(mediaId)) break;
          colorMediaMap[variantColor].add(mediaId);
        }
      });

      return {
        variantToColorMap,
        sharedMediaIds,
        colorMediaMap: Object.fromEntries(
          Object.entries(colorMediaMap).map(([color, mediaSet]) => [color, Array.from(mediaSet)])
        ),
      };
    }

    getCachedColorMediaLookup(variantMediaGroupingData = null, colorValues = [], normalize = null) {
      const source = this.host.variantMediaGroupingDataSource || '';
      const normalizedColors = Array.isArray(colorValues)
        ? colorValues
            .map((value) =>
              String(value || '')
                .trim()
                .toLowerCase()
            )
            .filter(Boolean)
            .sort()
        : [];
      const cacheKey = `${source}|${normalizedColors.join('|')}`;
      if (this.host.cachedColorMediaLookup && this.host.cachedColorMediaLookupKey === cacheKey) {
        return this.host.cachedColorMediaLookup;
      }
      const nextLookup = this.buildColorMediaLookup(variantMediaGroupingData, colorValues, normalize);
      this.host.cachedColorMediaLookupKey = cacheKey;
      this.host.cachedColorMediaLookup = nextLookup;
      return nextLookup;
    }

    getColorFilterDomSignature() {
      const mediaIds = Array.from(
        this.host.getPreferredGalleryList(this.host)?.querySelectorAll('li.product__media-item[data-media-id]') || []
      )
        .map((item) => item.dataset.mediaId)
        .filter(Boolean)
        .join(',');
      const thumbnailIds = Array.from(
        this.host.getPreferredThumbnailList(this.host)?.querySelectorAll('li.thumbnail-list__item[data-target]') || []
      )
        .map((item) => item.dataset.target)
        .filter(Boolean)
        .join(',');
      return `${mediaIds}|${thumbnailIds}`;
    }

    reorderColorFilteredItems(groups) {
      const visibleExact = groups.exact.filter((item) => item?.parentElement);
      const visibleShared = groups.shared.filter((item) => item?.parentElement);
      const hidden = groups.hidden.filter((item) => item?.parentElement);
      if (!visibleExact.length && !visibleShared.length) return;
      const ordered = visibleExact.concat(visibleShared, hidden);
      const parent = ordered[0]?.parentElement;
      if (!parent) return;
      ordered.forEach((item) => parent.appendChild(item));
    }

    refreshGalleryAfterColorFilter() {
      const galleryViewer = this.host.querySelector('media-gallery [id^="GalleryViewer"]');
      if (galleryViewer && typeof galleryViewer.resetPages === 'function') {
        galleryViewer.resetPages();
      }

      const mediaItems = Array.from(
        this.host.getPreferredGalleryList(this.host)?.querySelectorAll('li.product__media-item[data-media-id]') || []
      );
      mediaItems.forEach((item, index) => {
        if (item.getAttribute('data-color-filter-hidden') === 'true' || item.style.display === 'none') return;
        const image = item.querySelector('img[data-gallery-lazy-image="true"]');
        if (!image || image.dataset.galleryLazyHydrated === 'true') return;

        const dataSrc = image.getAttribute('data-src');
        const dataSrcset = image.getAttribute('data-srcset');
        const dataSizes = image.getAttribute('data-sizes');
        if (dataSrcset) image.setAttribute('srcset', dataSrcset);
        if (dataSizes) image.setAttribute('sizes', dataSizes);
        if (dataSrc) image.setAttribute('src', dataSrc);

        image.setAttribute('fetchpriority', index < 2 ? 'high' : 'low');
        if (index < 2) image.setAttribute('loading', 'eager');
        image.dataset.galleryLazyHydrated = 'true';
      });
    }

    getActiveThumbnailContext(thumbnailItems = []) {
      const activeThumbnail = thumbnailItems.find((item) => item.querySelector('button[aria-current]'));
      if (!activeThumbnail) return null;

      const isVisibleThumbnail = (item) =>
        item &&
        item.getAttribute('data-color-filter-hidden') !== 'true' &&
        item.style.display !== 'none';
      const visibleThumbnails = thumbnailItems.filter(isVisibleThumbnail);
      const exactThumbnails = visibleThumbnails.filter((item) => item.dataset.colorFilterMatch === 'exact');
      const sharedThumbnails = visibleThumbnails.filter((item) => item.dataset.colorFilterMatch === 'shared');

      return {
        matchType: activeThumbnail.dataset.colorFilterMatch || '',
        visibleIndex: visibleThumbnails.indexOf(activeThumbnail),
        exactIndex: exactThumbnails.indexOf(activeThumbnail),
        sharedIndex: sharedThumbnails.indexOf(activeThumbnail),
      };
    }

    captureActiveThumbnailContext() {
      const thumbnailItems = Array.from(
        this.host.getPreferredThumbnailList(this.host)?.querySelectorAll('li.thumbnail-list__item[data-target]') || []
      );
      const context = this.getActiveThumbnailContext(thumbnailItems);
      this.host.pendingColorThumbnailContext = context
        ? { ...context, capturedAt: Date.now() }
        : null;
    }

    getPreferredMediaIdForPreviousThumbnail(groups, previousThumbnailContext = null) {
      if (!previousThumbnailContext) return '';

      const exactItems = groups.exact || [];
      const sharedItems = groups.shared || [];
      const itemAt = (items, index) => {
        if (!items.length || index < 0) return null;
        return items[Math.min(index, items.length - 1)] || null;
      };

      if (previousThumbnailContext.matchType === 'exact') {
        return itemAt(exactItems, previousThumbnailContext.exactIndex)?.dataset.mediaId || '';
      }

      if (previousThumbnailContext.matchType === 'shared') {
        return itemAt(sharedItems, previousThumbnailContext.sharedIndex)?.dataset.mediaId || '';
      }

      if (previousThumbnailContext.visibleIndex > 0) {
        return itemAt(exactItems.concat(sharedItems), previousThumbnailContext.visibleIndex)?.dataset.mediaId || '';
      }

      return '';
    }

    syncActiveMediaToColorSelection(variant, groups, previousThumbnailContext = null) {
      const mediaGallery = this.host.querySelector('media-gallery');
      if (!mediaGallery) return;
      const galleryList = this.host.getPreferredGalleryList(this.host);
      if (!galleryList) return;
      const thumbnailList = this.host.getPreferredThumbnailList(this.host);

      const exactItems = groups.exact || [];
      const sharedItems = groups.shared || [];
      const activeItem = galleryList.querySelector('.product__media-item.is-active[data-media-id]');
      const activeId = activeItem?.dataset.mediaId || '';
      const activeHidden =
        !activeItem ||
        activeItem.getAttribute('data-color-filter-hidden') === 'true' ||
        activeItem.style.display === 'none';
      const exactIds = new Set(exactItems.map((item) => item.dataset.mediaId).filter(Boolean));

      let preferredMediaId = this.getPreferredMediaIdForPreviousThumbnail(groups, previousThumbnailContext);
      const featuredMediaId = variant?.featured_media?.id
        ? `${this.host.dataset.section}-${variant.featured_media.id}`
        : '';
      if (!preferredMediaId && featuredMediaId && exactIds.has(featuredMediaId)) {
        preferredMediaId = featuredMediaId;
      } else if (!preferredMediaId && exactItems[0]?.dataset.mediaId) {
        preferredMediaId = exactItems[0].dataset.mediaId;
      }

      if (!preferredMediaId && sharedItems[0]?.dataset.mediaId) {
        preferredMediaId = sharedItems[0].dataset.mediaId;
      }

      if (!preferredMediaId) return;
      const shouldSwitchActive = activeId !== preferredMediaId || activeHidden;
      if (!shouldSwitchActive) {
        this.scrollToGalleryAfterColorChange();
        return;
      }

      mediaGallery.setActiveMedia(preferredMediaId, false);

      const resolvedActiveItem = galleryList.querySelector(
        `.product__media-item.is-active[data-media-id="${preferredMediaId}"]`
      );
      if (resolvedActiveItem) {
        this.scrollToGalleryAfterColorChange();
        return;
      }

      const preferredItem = galleryList.querySelector(
        `.product__media-item[data-media-id="${preferredMediaId}"]`
      );
      if (!preferredItem) return;

      galleryList
        .querySelectorAll('.product__media-item.is-active[data-media-id]')
        .forEach((item) => item.classList.remove('is-active'));
      preferredItem.classList.add('is-active');

      if (!thumbnailList) {
        this.scrollToGalleryAfterColorChange();
        return;
      }
      thumbnailList.querySelectorAll('button[aria-current]').forEach((button) => button.removeAttribute('aria-current'));
      thumbnailList
        .querySelector(`.thumbnail-list__item[data-target="${preferredMediaId}"] button`)
        ?.setAttribute('aria-current', true);
      this.scrollToGalleryAfterColorChange();
    }

    scrollToGalleryAfterColorChange() {
      if (!this.host.pendingColorChangeScroll) return;
      this.host.pendingColorChangeScroll = false;
      if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
      const target = this.host.querySelector('media-gallery') || this.host.querySelector('[id^="GalleryViewer"]');
      if (!target || typeof target.scrollIntoView !== 'function') return;
      target.scrollIntoView({ behavior: 'auto', block: 'start' });
    }

    scheduleColorFilterSync(variant = null, options = {}) {
      const defer = options.defer === true;
      const apply = () => {
        if (!this.host.isConnected) return;
        const nextVariant = variant || this.host.getSelectedVariant(this.host);
        if (defer) {
          this.scheduleDeferredColorFilter(nextVariant);
        } else {
          this.applyGalleryColorFilter(nextVariant);
        }
      };

      apply();

      if (this.host.colorFilterSyncRafId) {
        cancelAnimationFrame(this.host.colorFilterSyncRafId);
      }
      this.host.colorFilterSyncRafId = requestAnimationFrame(() => {
        this.host.colorFilterSyncRafId = 0;
        apply();
      });

      const hasDuplicateMediaContainers =
        this.host.querySelectorAll('media-gallery [id^="GalleryViewer"]').length > 1 ||
        this.host.querySelectorAll('media-gallery [id^="GalleryThumbnails"]').length > 1;
      if (this.host.colorFilterSyncTimeoutId) {
        clearTimeout(this.host.colorFilterSyncTimeoutId);
      }
      if (!hasDuplicateMediaContainers) {
        this.host.colorFilterSyncTimeoutId = 0;
        return;
      }
      this.host.colorFilterSyncTimeoutId = setTimeout(() => {
        this.host.colorFilterSyncTimeoutId = 0;
        apply();
      }, 140);
    }

    filterGalleryBySelectedColor(variant = null, options = {}) {
      if (options.defer === true) {
        this.scheduleDeferredColorFilter(variant);
        return;
      }
      this.applyGalleryColorFilter(variant);
    }

    applyGalleryColorFilter(variant = null) {
      const color = this.getColorOptionContext(variant);
      if (!color?.selectedValue || !Array.isArray(color.values) || color.values.length < 2) {
        this.host.clearColorGalleryFilter(this.host);
        this.host.lastAppliedColorFilterKey = '';
        this.host.lastAppliedColorFilterDomSignature = '';
        return;
      }

      const normalize = (value) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[\/／]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      const selectedValue = normalize(color.selectedValue);
      const colorValues = color.values.map(normalize).filter(Boolean);
      if (!selectedValue || !colorValues.includes(selectedValue)) {
        this.host.clearColorGalleryFilter(this.host);
        this.host.lastAppliedColorFilterKey = '';
        this.host.lastAppliedColorFilterDomSignature = '';
        return;
      }
      const domSignature = this.getColorFilterDomSignature();
      const filterKey = `${selectedValue}|${colorValues.join('|')}`;
      if (
        this.host.lastAppliedColorFilterKey === filterKey &&
        this.host.lastAppliedColorFilterDomSignature === domSignature
      ) {
        return;
      }
      if (this.host.lastSyncedColorFilterDomSignature !== domSignature) {
        this.host.syncDuplicateMediaListMarkup();
        this.host.lastSyncedColorFilterDomSignature = domSignature;
      }
      this.host.lastAppliedColorFilterKey = filterKey;

      const orderedVariantMediaContext = this.getOrderedVariantMediaContext(variant);
      const variantMediaGroupingData = this.getVariantMediaGroupingData();
      const colorMediaLookup = this.getCachedColorMediaLookup(variantMediaGroupingData, colorValues, normalize);
      const selectedVariantId = variant?.id ? String(variant.id) : '';
      const mappedColorValue = selectedValue || colorMediaLookup?.variantToColorMap?.[selectedVariantId] || '';
      const exactMediaIds = new Set(orderedVariantMediaContext?.exactIds || colorMediaLookup?.colorMediaMap?.[mappedColorValue] || []);
      const sharedMediaIds = new Set(colorMediaLookup?.sharedMediaIds || []);
      const hasExactMediaMapping = exactMediaIds.size > 0;
      const hasUsableMediaMapping = exactMediaIds.size > 0 || sharedMediaIds.size > 0;
      const otherColorValues = colorValues.filter((value) => value !== selectedValue);

      const classifyItemsByColorMedia = (items) => {
        const exact = [];
        const shared = [];
        const hidden = [];

        items.forEach((item) => {
          const mediaId = item.dataset.mediaId || item.dataset.target || '';
          const rawMediaId = this.host.getRawMediaId(mediaId);

          let matchType = 'hidden';
          let matchSource = 'media-id-map';
          if (exactMediaIds.size && rawMediaId && exactMediaIds.has(rawMediaId)) {
            matchType = 'exact';
          } else if (sharedMediaIds.size && rawMediaId && sharedMediaIds.has(rawMediaId)) {
            matchType = 'shared';
          } else if (hasExactMediaMapping && rawMediaId) {
            matchType = 'hidden';
          } else {
            matchSource = 'alt-fallback';
            const image = item.querySelector('img');
            const rawAlt = image?.getAttribute('alt') || '';
            const normalizedAlt = normalize(rawAlt);
            const hasSelectedColorInAlt = normalizedAlt && normalizedAlt.includes(selectedValue);
            const hasOtherColorInAlt =
              normalizedAlt && otherColorValues.some((colorValue) => normalizedAlt.includes(colorValue));

            if (hasSelectedColorInAlt) {
              matchType = 'exact';
            } else if (hasOtherColorInAlt) {
              matchType = 'hidden';
            } else if (this.host.getCurrentProductIdentityTerms().some((term) => normalizedAlt.includes(term))) {
              matchType = 'shared';
            } else if (!hasUsableMediaMapping) {
              matchType = 'shared';
            }
          }

          item.classList.toggle('color-filter-hidden', matchType === 'hidden');
          item.setAttribute('data-color-filter-hidden', matchType === 'hidden' ? 'true' : 'false');
          item.dataset.colorFilterMatch = matchType;
          item.dataset.colorFilterSource = matchSource;
          item.style.display = matchType === 'hidden' ? 'none' : '';

          if (matchType === 'exact') {
            exact.push(item);
          } else if (matchType === 'shared') {
            shared.push(item);
          } else {
            hidden.push(item);
          }
        });

        return { exact, shared, hidden };
      };

      const sortByExactOrder = (groups, key) => {
        if (!orderedVariantMediaContext?.exactIdSet?.size || !Array.isArray(orderedVariantMediaContext?.exactIds)) return;
        const orderIndex = new Map(orderedVariantMediaContext.exactIds.map((id, index) => [String(id), index]));
        groups.exact.sort((a, b) => {
          const aKey = this.host.getRawMediaId(a.dataset[key] || '');
          const bKey = this.host.getRawMediaId(b.dataset[key] || '');
          return (orderIndex.get(aKey) ?? Number.MAX_SAFE_INTEGER) - (orderIndex.get(bKey) ?? Number.MAX_SAFE_INTEGER);
        });
      };

      const mediaItems = Array.from(
        this.host.getPreferredGalleryList(this.host)?.querySelectorAll('li.product__media-item[data-media-id]') || []
      );
      const thumbnailItems = Array.from(
        this.host.getPreferredThumbnailList(this.host)?.querySelectorAll('li.thumbnail-list__item[data-target]') || []
      );
      const capturedThumbnailContext =
        this.host.pendingColorThumbnailContext &&
        Date.now() - this.host.pendingColorThumbnailContext.capturedAt < 4000
          ? this.host.pendingColorThumbnailContext
          : null;
      const previousThumbnailContext = capturedThumbnailContext || this.getActiveThumbnailContext(thumbnailItems);

      if (!mediaItems.length && !thumbnailItems.length) {
        this.host.lastAppliedColorFilterDomSignature = '';
        return;
      }

      const mediaGroups = classifyItemsByColorMedia(mediaItems);
      const thumbnailGroups = classifyItemsByColorMedia(thumbnailItems);

      sortByExactOrder(mediaGroups, 'mediaId');
      sortByExactOrder(thumbnailGroups, 'target');

      this.reorderColorFilteredItems(mediaGroups);
      this.reorderColorFilteredItems(thumbnailGroups);
      this.syncActiveMediaToColorSelection(variant, mediaGroups, previousThumbnailContext);
      this.refreshGalleryAfterColorFilter();

      this.host.lastAppliedColorFilterKey = filterKey;
      this.host.lastAppliedColorFilterDomSignature = this.getColorFilterDomSignature();
    }
  }

  window.RiseProductInfoMediaControllers = {
    ProductGalleryDomSyncController,
    ProductColorMediaFilterController,
  };
  window.dispatchEvent(new CustomEvent('rise:product-media-controllers-ready'));
})();
